import React, { useState, useEffect, useMemo } from "react";
import { Modal, TextInput, Button, Group, Image, Text, Box } from "@mantine/core";
import { IconSearch, IconCheck } from "@tabler/icons-react";
import {
  MantineReactTable,
  useMantineReactTable,
  MRT_ColumnDef,
  MRT_PaginationState,
} from "mantine-react-table";
import { convertImageDomain } from "../../../const/converhandle";
import Repository from "../../../_base/helper/HttpHelper";
import { API_ROUTE } from "../../../const/apiRoute";
import { MessageResponse } from "../../../model/MessageResponse";
import { _localization } from "../../../config/location";

interface BrandSelectModalProps {
  opened: boolean;
  onClose: () => void;
  onSelect: (brand: { value: string; label: string; logo: string }) => void;
}

const BrandSelectModal = ({
  opened,
  onClose,
  onSelect,
}: BrandSelectModalProps) => {
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [tempSearchKeyword, setTempSearchKeyword] = useState("");
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const repository = new Repository(process.env.REACT_APP_Demo_APP_API_URL);
      let url = `${API_ROUTE.GET_LIST_BRAND}?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}`;
      if (searchKeyword) {
        url += `&KeySearch=${encodeURIComponent(searchKeyword)}`;
      }
      const response = await repository.get<MessageResponse<any>>(url);
      if (response?.data?.lists) {
        setData(response.data.lists);
        setTotal(response.data.count || response.data.lists.length);
      } else {
        setData([]);
        setTotal(0);
      }
    } catch (e) {
      console.error(e);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (opened) {
      fetchData();
    }
  }, [opened, pagination.pageIndex, pagination.pageSize, searchKeyword]);

  const handleSearch = () => {
    setSearchKeyword(tempSearchKeyword);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "image",
        header: "Logo",
        size: 120,
        Cell: ({ row }) => (
          <Box
            style={{
              width: 80,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #eee",
              borderRadius: 4,
              padding: 4,
              background: "#fff"
            }}
          >
            <Image
              src={`${convertImageDomain(row.original.imageUrl)}/${
                row.original.image
              }`}
              fit="contain"
              fallbackSrc="https://via.placeholder.com/80x40?text=No+Logo"
            />
          </Box>
        ),
      },
      {
        accessorKey: "name",
        header: "Tên thương hiệu",
        size: 300,
        Cell: ({ cell }) => (
          <Text fw={500} size="sm">
            {cell.getValue<string>()}
          </Text>
        )
      },
      {
        accessorKey: "action",
        header: "Thao tác",
        size: 100,
        Cell: ({ row }) => (
          <Button
            size="xs"
            color="blue"
            variant="filled"
            leftSection={<IconCheck size={14} />}
            style={{ borderRadius: "6px" }}
            onClick={() => {
              onSelect({
                value: row.original.id.toString(),
                label: row.original.name,
                logo: `${convertImageDomain(row.original.imageUrl)}/${
                  row.original.image
                }`,
              });
              onClose();
            }}
          >
            Chọn
          </Button>
        ),
      },
    ],
    [onSelect, onClose]
  );

  const table = useMantineReactTable({
    columns,
    data,
    rowCount: total,
    manualPagination: true,
    state: {
      isLoading,
      pagination,
    },
    onPaginationChange: setPagination,
    enableRowSelection: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableSorting: false,
    enableTopToolbar: false,
    initialState: {
      density: "xs",
    },
    localization: _localization,
    mantineTableProps: {
      withColumnBorders: true,
      withTableBorder: true,
      striped: "even",
      highlightOnHover: true,
    },
    mantineTableContainerProps: {
      style: { maxHeight: "400px" },
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={700} size="lg" c="white">CHỌN THƯƠNG HIỆU</Text>}
      size="xl"
      centered
      zIndex={2001}
      styles={{
        content: { width: "800px", maxWidth: "100%" },
        header: { borderBottom: "1px solid #eee", paddingBottom: "12px", marginBottom: "16px" }
      }}
    >
      <Group mb="md" gap="sm">
        <TextInput
          placeholder="Nhập tên thương hiệu để tìm kiếm..."
          value={tempSearchKeyword}
          onChange={(e) => setTempSearchKeyword(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
          }}
          leftSection={<IconSearch size={16} color="#15aabf" />}
          style={{ flex: 1 }}
          styles={{ input: { borderRadius: "6px", height: "36px" } }}
        />
        <Button
          onClick={handleSearch}
          color="teal"
          style={{ borderRadius: "6px", height: "36px" }}
        >
          Tìm kiếm
        </Button>
      </Group>
      <MantineReactTable table={table} />
    </Modal>
  );
};

export default BrandSelectModal;
